/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import { FormBuilder } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { L10nService } from '@vmw/ngx-vip';

import { every } from 'underscore';

import { ConnectedPosition } from '@angular/cdk/overlay';
import { TOP_LEFT_ALIGNED_CONNECTED_POSITION } from 'ng/shared/components/avi-dropdown-button';

import {
    AviDataGridFieldVisibility,
    IAviDataGridConfigField,
    TAviDataGridColumnPresenceMap,
} from '../../avi-data-grid/avi-data-grid.types';

import * as l10n from './avi-data-grid-column-selection.component.l10n';

import './avi-data-grid-column-selection.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component for the pop-up column selection panel for the avi-data-grid family.
 * @author Zhiqian Liu, alextsg
 */
@Component({
    selector: 'avi-data-grid-column-selection',
    templateUrl: './avi-data-grid-column-selection.component.html',
})
export class AviDataGridColumnSelectionComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * Fields to hide/show.
     */
    @Input()
    public fields: IAviDataGridConfigField[];

    /**
     * Map to decide which field columns to show and which to hide.
     */
    @Input()
    public showColumnHash: TAviDataGridColumnPresenceMap = {};

    @Output()
    public onShowColumnHashChange = new EventEmitter<TAviDataGridColumnPresenceMap>();

    /**
     * Indicate whether all field columns are present.
     */
    public allFieldColumnsShown = false;

    /**
     * Positions of the column selection panel relative to the hide/show button. Contains only one
     * entry of the primary postion.
     */
    public viewColumnPanelPositions: [ConnectedPosition] = [TOP_LEFT_ALIGNED_CONNECTED_POSITION];

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Form group for checkboxes corresponding to grid fields.
     */
    public fieldsFormGroup = this.formBuilder.group({});

    /**
     * Subject to control the hide/show column selection panel close button.
     */
    private columnSelectionCloseControlSubject = new Subject<boolean>();

    /**
     * Used to end subscriptions.
     */
    private ngDestroyed$ = new Subject();

    /**
     * Return an observable to allow hide/show column panel to close itself through subscription.
     */
    public get columnSelectionCloseButtonControl$(): Observable<boolean> {
        return this.columnSelectionCloseControlSubject.asObservable();
    }

    public constructor(
        l10nService: L10nService,
        private readonly formBuilder: FormBuilder,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.setFieldsFormGroup();

        this.fieldsFormGroup.valueChanges
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(() => {
                const latestShowColumnHash = this.fieldsFormGroup.getRawValue();

                this.onShowColumnHashChange.emit(latestShowColumnHash);
                this.checkAllColumnsShown(latestShowColumnHash);
            });
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.fields && !changes.fields.isFirstChange()) {
            this.setFieldsFormGroup();
        }
    }

    /**
     * @override
     * Complete the ngDestroyed$ stream to destroy existing streams.
     */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Show all field columns by setting all values in the formGroup to true.
     */
    public showAllFieldColumns(): void {
        const showColumnHashValues: Record<string, boolean> = this.fieldsFormGroup.getRawValue();

        Object.keys(showColumnHashValues).forEach(field => showColumnHashValues[field] = true);
        this.fieldsFormGroup.setValue(showColumnHashValues);
    }

    /**
     * Close the column hide/show panel.
     */
    public closeColumnSelectionPanel(): void {
        this.columnSelectionCloseControlSubject.next(false);
    }

    private checkAllColumnsShown(showColumnHash: TAviDataGridColumnPresenceMap): void {
        this.allFieldColumnsShown = every(showColumnHash, shown => shown);
    }

    /**
     * Remove existing form controls from the form group then freshly adds controls back to the form
     * group. Used to reset things on initialization and when the fields @Input() binding has
     * changed.
     */
    private setFieldsFormGroup(): void {
        Object.keys(this.fieldsFormGroup.controls).forEach(key => {
            this.fieldsFormGroup.removeControl(key);
        });

        this.checkAllColumnsShown(this.showColumnHash);

        this.fields.forEach(field => {
            const control = this.formBuilder.control(this.showColumnHash[field.id]);

            if (field.visibility === AviDataGridFieldVisibility.MANDATORY) {
                control.disable();
            }

            this.fieldsFormGroup.addControl(field.id, control);
        });
    }
}
